import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Section, Container, SectionTitle, SectionDescription } from '../styles';
import { slateColors, purpleColors, violetColors } from '../styles';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

type SupportedCountryKey =
  | 'germany'
  | 'italy'
  | 'france'
  | 'austria'
  | 'switzerland'
  | 'belgium'
  | 'netherlands'
  | 'denmark'
  | 'hungary'
  | 'poland';

// Helper function to interpolate between two values
const lerp = (start: number, end: number, t: number): number => {
  return start + (end - start) * t;
};

// Helper function to convert HSL to RGB
const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h /= 360;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return [f(0), f(8), f(4)];
};

// Helper function to convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
};

// Helper function to get color based on punctuality rate (59% to 98%)
const getColorForRate = (rate: number): { bgColor: string; accentColor: string } => {
  // Normalize rate from 59-98% to 0-1 scale
  const normalized = Math.max(0, Math.min(1, (rate - 59) / (98 - 59)));
  
  // Define color stops in HSL (Hue, Saturation, Lightness)
  // Red (0) -> Orange (30) -> Yellow (55) -> Light Green (100) -> Green (140)
  // Adjusted to make 80% range more orange/yellow
  const colorStops = [
    { h: 0, s: 0.75, l: 0.5 },   // Red
    { h: 20, s: 0.85, l: 0.5 },  // Orange
    { h: 21, s: 0.85, l: 0.5 },  // Yellow/Amber (extended)
    { h: 50, s: 0.70, l: 0.5 },  // Light Green (moved further right)
    { h: 140, s: 0.70, l: 0.45 }, // Green
  ];
  
  // Find which segment we're in and interpolate
  const segmentCount = colorStops.length - 1;
  const segmentIndex = Math.min(
    Math.floor(normalized * segmentCount),
    segmentCount - 1
  );
  const segmentT = (normalized * segmentCount) - segmentIndex;
  
  const startColor = colorStops[segmentIndex];
  const endColor = colorStops[segmentIndex + 1];
  
  // Interpolate HSL values
  const h = lerp(startColor.h, endColor.h, segmentT);
  const s = lerp(startColor.s, endColor.s, segmentT);
  const l = lerp(startColor.l, endColor.l, segmentT);
  
  // Convert to RGB and then to hex for accent color
  const [r, g, b] = hslToRgb(h, s, l);
  const accentColor = rgbToHex(r, g, b);
  
  // Create background color using the same hue but with:
  // - Moderate saturation (0.15 to 0.25) for visible tint
  // - High lightness (0.94 to 0.96) for light background
  // - Higher opacity (0.6 to 0.85) that increases with better punctuality
  const bgH = h;
  const bgS = lerp(0.50, 0.60, normalized);
  const bgL = lerp(0.90, 0.94, normalized);
  const bgOpacity = lerp(0.6, 0.85, normalized);
  
  const [bgR, bgG, bgB] = hslToRgb(bgH, bgS, bgL);
  
  return {
    bgColor: `rgba(${bgR}, ${bgG}, ${bgB}, ${bgOpacity})`,
    accentColor,
  };
};

const punctualityStats: Array<{
  key: SupportedCountryKey;
  flag: string;
  rate: number;
  rateDisplay: string;
  bgColor: string;
  accentColor: string;
}> = [
  { key: 'germany', flag: '🇩🇪', rate: 58.55, rateDisplay: '59%', ...getColorForRate(58.55) },
  { key: 'poland', flag: '🇵🇱', rate: 61, rateDisplay: '61%', ...getColorForRate(61) },
  { key: 'italy', flag: '🇮🇹', rate: 62, rateDisplay: '62%', ...getColorForRate(62) },
  { key: 'france', flag: '🇫🇷', rate: 79.7, rateDisplay: '80%', ...getColorForRate(79.7) },
  { key: 'austria', flag: '🇦🇹', rate: 82.17, rateDisplay: '82%', ...getColorForRate(82.17) },
  { key: 'hungary', flag: '🇭🇺', rate: 86, rateDisplay: '86%', ...getColorForRate(86) },
  { key: 'belgium', flag: '🇧🇪', rate: 88.62, rateDisplay: '89%', ...getColorForRate(88.62) },
  { key: 'netherlands', flag: '🇳🇱', rate: 93.91, rateDisplay: '94%', ...getColorForRate(93.91) },
  { key: 'denmark', flag: '🇩🇰', rate: 96, rateDisplay: '96%', ...getColorForRate(96) },
  { key: 'switzerland', flag: '🇨🇭', rate: 97.79, rateDisplay: '98%', ...getColorForRate(97.79) },
];

type SupportedRegionsProps = {};

const SupportedRegionsSection = styled(Section)`
  background: linear-gradient(to bottom, ${purpleColors[50]}, ${violetColors[500]}15, ${purpleColors[50]});
  text-align: center;
`;

const RegionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  text-align: left;
  margin-top: 2.5rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const CountryCard = styled.div<{ bgColor: string }>`
  border-radius: 1rem;
  border: 1px solid ${slateColors[100]};
  background: ${props => props.bgColor};
  padding: 1rem 1.25rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const CountryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const CountryName = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[800]};
`;

const Label = styled.p`
  font-size: 1rem;
  color: ${slateColors[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  margin-top: 0.2rem;
`;

const Rate = styled.p<{ accentColor: string }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.accentColor};
  line-height: 1;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

const CTACard = styled.div`
  border-radius: 1rem;
  border: 1px solid ${slateColors[100]};
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  padding: 1rem 1.25rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const CTAHeading = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${slateColors[800]};
  margin-bottom: 0.5rem;
  margin-top: 0;
`;

const CTADescription = styled.p`
  font-size: 0.875rem;
  color: ${slateColors[600]};
  margin-bottom: 1rem;
  line-height: 1.5;
  margin-top: 0;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${purpleColors[500]});
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  transition: all 0.2s;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
  align-self: flex-start;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
    
    svg {
      transform: translateX(0.25rem);
    }
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s;
  }
`;

export default function SupportedRegions({}: SupportedRegionsProps) {
  const { t } = useTranslation();

  return (
    <SupportedRegionsSection>
      <Container style={{ maxWidth: '64rem' }}>
        <SectionTitle>{t('appLandingPage.supportedRegions.heading')}</SectionTitle>
        <SectionDescription style={{ fontSize: '1.125rem', lineHeight: '1.6' }}>
          {t('appLandingPage.supportedRegions.description')}
        </SectionDescription>
        <RegionsGrid>
          {punctualityStats.map(({ key, flag, rateDisplay, bgColor, accentColor }) => {
            const countryName = t(`appLandingPage.supportedRegions.countries.${key}`);
            return (
              <CountryCard key={key} bgColor={bgColor}>
                <CountryHeader>
                  <span role="img" aria-label={`${countryName} flag`}>
                    {flag}
                  </span>
                  <CountryName>{countryName}</CountryName>
                </CountryHeader>
                <Label>{t('appLandingPage.supportedRegions.label')}</Label>
                <Rate accentColor={accentColor}>{rateDisplay}</Rate>
              </CountryCard>
            );
          })}
          <CTACard>
            <div>
              <CTAHeading>{t('appLandingPage.supportedRegions.cta.heading')}</CTAHeading>
              <CTADescription>{t('appLandingPage.supportedRegions.cta.description')}</CTADescription>
            </div>
            <CTAButton to="/2024wrapped">
              {t('appLandingPage.supportedRegions.cta.button')}
              <ArrowRight />
            </CTAButton>
          </CTACard>
        </RegionsGrid>
      </Container>
    </SupportedRegionsSection>
  );
}

